<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import {
    fetchTableViews,
    createTableView,
    updateTableView,
    deleteTableView,
    type ApiTableView,
  } from '../lib/data-tables-api';

  let {
    slug,
    /** Поточний стан таблиці для збереження */
    currentColumns,
    currentSortKey,
    currentFilters,
    /** Викликається коли обраний вид треба застосувати */
    onApply,
    isAdmin = false,
  }: {
    slug: string;
    currentColumns: string[];
    currentSortKey: string | null | undefined;
    currentFilters: unknown;
    onApply: (view: ApiTableView) => void;
    isAdmin?: boolean;
  } = $props();

  let open = $state(false);
  let views = $state<ApiTableView[]>([]);
  let loading = $state(true);
  let busy = $state(false);
  let saveMode = $state(false);
  let saveName = $state('');
  let saveAsDefault = $state(false);
  let editingId = $state<string | null>(null);
  let editingName = $state('');

  async function load() {
    loading = true;
    try {
      const r = await fetchTableViews(slug);
      views = r.items;
    } catch {
      views = [];
    } finally {
      loading = false;
    }
  }

  onMount(load);

  async function onSave() {
    if (!saveName.trim()) return;
    busy = true;
    try {
      const created = await createTableView(slug, {
        name: saveName.trim(),
        columns: currentColumns,
        sortKey: currentSortKey ?? null,
        filters: currentFilters ?? null,
        isDefault: saveAsDefault,
      });
      views = saveAsDefault
        ? [created, ...views.map((v) => ({ ...v, isDefault: false }))]
        : [...views, created];
      saveName = '';
      saveAsDefault = false;
      saveMode = false;
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onUpdate(v: ApiTableView) {
    busy = true;
    try {
      const updated = await updateTableView(slug, v.id, {
        columns: currentColumns,
        sortKey: currentSortKey ?? null,
        filters: currentFilters ?? null,
      });
      views = views.map((x) => (x.id === v.id ? updated : x));
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onRename(v: ApiTableView) {
    if (!editingName.trim() || editingName.trim() === v.name) {
      editingId = null;
      return;
    }
    busy = true;
    try {
      const updated = await updateTableView(slug, v.id, { name: editingName.trim() });
      views = views.map((x) => (x.id === v.id ? updated : x));
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
      editingId = null;
    }
  }

  async function onSetDefault(v: ApiTableView) {
    busy = true;
    try {
      await updateTableView(slug, v.id, { isDefault: true });
      views = views.map((x) => ({ ...x, isDefault: x.id === v.id }));
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onDelete(v: ApiTableView) {
    if (!confirm(`Видалити вид "${v.name}"?`)) return;
    busy = true;
    try {
      await deleteTableView(slug, v.id);
      views = views.filter((x) => x.id !== v.id);
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  function apply(v: ApiTableView) {
    onApply(v);
    open = false;
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false;
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="relative">
  <button
    type="button"
    onclick={() => (open = !open)}
    class="flex items-center gap-1.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-[var(--color-bg-3)]"
    class:bg-purple-50={views.some((v) => v.isDefault)}
  >
    <span class="inline-flex"><Icon name="bookmark" size={14} /></span>
    <span class="hidden xs:inline">Збережені види</span>
    {#if views.length > 0}
      <span class="inline-block rounded-full bg-[var(--color-pu)] text-white text-[9px] font-bold px-1.5 py-0.5 leading-none min-w-[16px] text-center">{views.length}</span>
    {/if}
  </button>

  {#if open}
    <!-- Backdrop -->
    <div
      class="fixed inset-0 z-30"
      role="presentation"
      onclick={() => (open = false)}
    ></div>

    <div class="absolute right-0 top-full mt-1.5 z-40 w-72 rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl overflow-hidden">
      <!-- Header -->
      <div class="px-3.5 py-2.5 border-b border-[var(--color-border-3)] flex items-center justify-between">
        <span class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Збережені види</span>
        {#if isAdmin && !saveMode}
          <button
            type="button"
            onclick={() => (saveMode = true)}
            class="text-[10.5px] font-semibold text-[var(--color-pu-3)] hover:underline"
          >+ Зберегти поточний</button>
        {/if}
      </div>

      <!-- Save form -->
      {#if saveMode}
        <div class="px-3.5 py-3 border-b border-[var(--color-border-3)] space-y-2">
          <input
            type="text"
            bind:value={saveName}
            placeholder="Назва виду…"
            autofocus
            class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs"
            onkeydown={(e) => e.key === 'Enter' && onSave()}
          />
          <label class="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <input type="checkbox" bind:checked={saveAsDefault} class="rounded" />
            Зробити основним (завантажується при відкритті)
          </label>
          <div class="flex gap-2">
            <button
              type="button"
              onclick={onSave}
              disabled={busy || !saveName.trim()}
              class="flex-1 rounded-md bg-[var(--color-pu)] text-white py-1.5 text-[11px] font-semibold disabled:opacity-50"
            >Зберегти</button>
            <button
              type="button"
              onclick={() => (saveMode = false)}
              class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-[11px]"
            >Скасувати</button>
          </div>
        </div>
      {/if}

      <!-- Views list -->
      {#if loading}
        <div class="px-4 py-4 text-center text-xs text-[var(--color-text-muted)]">Завантаження…</div>
      {:else if views.length === 0}
        <div class="px-4 py-5 text-center text-xs text-[var(--color-text-muted-2)] italic">
          Ще немає збережених видів.<br />
          {#if isAdmin}Натисніть «Зберегти поточний» щоб створити перший.{/if}
        </div>
      {:else}
        <div class="max-h-64 overflow-y-auto divide-y divide-[var(--color-border-3)]">
          {#each views as v (v.id)}
            <div class="px-3.5 py-2 flex items-center gap-2 hover:bg-[var(--color-bg-3)]/50 group">
              <!-- Name / rename input -->
              <div class="flex-1 min-w-0">
                {#if editingId === v.id}
                  <input
                    type="text"
                    bind:value={editingName}
                    class="w-full rounded border border-[var(--color-border-3)] px-1.5 py-0.5 text-xs"
                    autofocus
                    onblur={() => onRename(v)}
                    onkeydown={(e) => { if (e.key === 'Enter') onRename(v); if (e.key === 'Escape') editingId = null; }}
                  />
                {:else}
                  <button
                    type="button"
                    onclick={() => apply(v)}
                    class="w-full text-left text-xs font-medium truncate"
                  >
                    {#if v.isDefault}<span class="mr-1 inline-flex align-[-2px] text-[var(--color-pu-3)]"><Icon name="star" size={12} /></span>{/if}
                    {v.name}
                  </button>
                  <div class="text-[10px] text-[var(--color-text-muted-2)]">
                    {v.columns.length} кол. · {v.sortKey ?? 'без сорт.'}
                  </div>
                {/if}
              </div>

              {#if isAdmin}
                <!-- Action buttons (visible on hover) -->
                <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <!-- Update current state into this view -->
                  <button
                    type="button"
                    onclick={() => onUpdate(v)}
                    disabled={busy}
                    title="Оновити вид поточним станом"
                    class="inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--color-border-3)] hover:border-[var(--color-pu-2)] hover:text-[var(--color-pu-3)] disabled:opacity-40"
                  ><Icon name="arrowUp" size={12} /></button>
                  <!-- Rename -->
                  <button
                    type="button"
                    onclick={() => { editingId = v.id; editingName = v.name; }}
                    title="Перейменувати"
                    class="inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--color-border-3)] hover:border-[var(--color-pu-2)] hover:text-[var(--color-pu-3)]"
                  ><Icon name="edit" size={12} /></button>
                  <!-- Set default -->
                  {#if !v.isDefault}
                    <button
                      type="button"
                      onclick={() => onSetDefault(v)}
                      disabled={busy}
                      title="Зробити основним"
                      class="inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--color-border-3)] hover:border-[var(--color-pu-2)] hover:text-[var(--color-pu-3)] disabled:opacity-40"
                    ><Icon name="star" size={12} /></button>
                  {/if}
                  <!-- Delete -->
                  <button
                    type="button"
                    onclick={() => onDelete(v)}
                    disabled={busy}
                    title="Видалити вид"
                    class="inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--color-border-3)] hover:border-red-300 hover:text-red-600 disabled:opacity-40"
                  ><Icon name="close" size={12} /></button>
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>
